import React, { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { isServerSide } from 'utility/constant';
import { useAppDispatch } from 'lib/centralStore';
import { initialBoundingRect } from 'features/settings/type';
import { updatePosition, updateSize } from 'features/settings/settingsSlice';
import { useIsomorphicLayoutEffect } from 'hooks';

const EVENT_THRESHOLD = 149;
// use it only in _app file

const useBoundingRectHook = () => {
  const dispatch = useAppDispatch();

  const { isReady, events } = useRouter();

  const tmrSizeRef = useRef<NodeJS.Timeout>();
  const tmrPositionRef = useRef<NodeJS.Timeout>();

  const { top, left, width, height } = initialBoundingRect;
  const prevTop = useRef(top);
  const prevLeft = useRef(left);
  const prevWidth = useRef(width);
  const prevHeight = useRef(height);

  const onScrollHandler = useCallback(() => {
    if (isServerSide) return;

    const doApply = () => {
      const { top, left } = document.documentElement.getBoundingClientRect();
      prevTop.current = top;
      prevLeft.current = left;
      dispatch(updatePosition({ top, left }));
    };

    clearTimeout(tmrPositionRef.current);
    const rect = document.documentElement.getBoundingClientRect();

    let doSet = false;
    if (Math.abs(rect.top - (prevTop.current ?? 0)) > EVENT_THRESHOLD) {
      doSet = true;
    } else if (Math.abs(rect.left - (prevLeft.current ?? 0)) > EVENT_THRESHOLD) {
      doSet = true;
    }

    if (doSet) {
      // trigger update by gap
      doApply();
    } else {
      // debounced trigger
      tmrPositionRef.current = setTimeout(doApply, EVENT_THRESHOLD);
    }
  }, [dispatch]);

  const onResizeHandler = useCallback(() => {
    if (isServerSide) return;

    const doApply = () => {
      const { height, width } = document.documentElement.getBoundingClientRect();
      prevWidth.current = width;
      prevHeight.current = height;
      dispatch(updateSize({ height, width }));
    };

    clearTimeout(tmrSizeRef.current);
    const rect = document.documentElement.getBoundingClientRect();

    let doSet = false;
    if (Math.abs(rect.width - (prevWidth.current ?? 0)) > EVENT_THRESHOLD) {
      doSet = true;
    } else if (Math.abs(rect.height - (prevHeight.current ?? 0)) > EVENT_THRESHOLD) {
      doSet = true;
    }

    if (doSet) {
      // trigger update by gap
      doApply();
    } else {
      // debounced trigger
      tmrSizeRef.current = setTimeout(doApply, EVENT_THRESHOLD);
    }
  }, [dispatch]);

  const doInit = useCallback(() => {
    onScrollHandler();
    onResizeHandler();
  }, [onScrollHandler, onResizeHandler]);

  useEffect(() => {
    if (isServerSide) return;
    window.addEventListener('scroll', onScrollHandler, { passive: true });
    window.addEventListener('resize', onResizeHandler, { passive: true });

    return () => {
      if (isServerSide) return;
      clearTimeout(tmrSizeRef.current);
      clearTimeout(tmrPositionRef.current);

      window.removeEventListener('resize', onResizeHandler);
      window.removeEventListener('scroll', onScrollHandler);
    };
  }, [doInit, onResizeHandler, onScrollHandler]);

  useEffect(() => {
    events.on('routeChangeComplete', doInit);
    return () => {
      events.off('routeChangeComplete', doInit);
    };
  }, [events, doInit]);

  useIsomorphicLayoutEffect(() => {
    if (isReady) {
      doInit();
    }
  }, [isReady, doInit]);
};

export const BoundaryClientHandler = () => {
  useBoundingRectHook();
  return <React.Fragment key={`boundary-client-handler`} />;
};
